import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import { companyInfo, aboutData } from '@/mocks/home';
import { useScrollAnimation, useCountUp } from '@/hooks/useScrollAnimation';
import JsonLd, { getOrganizationSchema, getWebPageSchema, getBreadcrumbSchema } from '@/utils/seo';

const missionValues = [
  { icon: 'ri-eye-line', title: 'Our Vision', text: 'To be Bangalore&apos;s most trusted one-stop property solutions provider, transforming spaces and enhancing lives through quality craftsmanship and exceptional service.' },
  { icon: 'ri-flag-line', title: 'Our Mission', text: 'To deliver comprehensive, high-quality property development, renovation, and maintenance services under one roof — saving our clients time, money, and stress while exceeding their expectations on every project.' },
  { icon: 'ri-heart-line', title: 'Our Values', text: 'Quality without compromise, transparency in every transaction, respect for deadlines, continuous improvement, and genuine care for our clients&apos; vision and satisfaction.' },
];

const whatSetsUsApart = [
  { icon: 'ri-stack-line', title: 'One-Stop Solution', description: 'From landscaping to interiors, civil works to deep cleaning — everything under one roof. No need to coordinate with multiple vendors.' },
  { icon: 'ri-user-star-line', title: 'Experienced Professionals', description: 'Our team comprises skilled designers, engineers, craftsmen, and project managers with decades of combined experience across diverse property types.' },
  { icon: 'ri-shield-check-line', title: 'Quality Guaranteed', description: 'We use only premium materials sourced from trusted suppliers. Every project undergoes rigorous quality checks before handover.' },
  { icon: 'ri-time-line', title: 'Timely Delivery', description: 'We respect deadlines. Our project management approach ensures your project is completed on schedule without compromising quality.' },
  { icon: 'ri-price-tag-3-line', title: 'Transparent Pricing', description: 'No hidden costs, no surprises. We provide detailed quotations upfront and keep you informed throughout the project.' },
  { icon: 'ri-customer-service-2-line', title: 'After-Service Support', description: 'Our relationship does not end at project handover. We offer AMC packages and responsive after-service support for your peace of mind.' },
];

const industries = [
  'Independent Houses', 'Apartments', 'Villas', 'Commercial Buildings',
  'Hospitals', 'Hotels', 'Resorts', 'Retail Stores',
  'Corporate Offices', 'Educational Institutions', 'Industrial Buildings',
];

function StatCounter({ value, label, isVisible }: { value: number; label: string; isVisible: boolean }) {
  const count = useCountUp(value, 2000, isVisible);
  const displayValue = value >= 100 ? `${count}+` : `${count}%`;
  return (
    <div className="text-center">
      <div className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-2">
        {displayValue}
      </div>
      <div className="text-sm md:text-base text-white/80">{label}</div>
    </div>
  );
}

export default function AboutPage() {
  const { ref: storyRef, isVisible: storyVisible } = useScrollAnimation(0.05);
  const { ref: missionRef, isVisible: missionVisible } = useScrollAnimation(0.05);
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation(0.08);
  const { ref: apartRef, isVisible: apartVisible } = useScrollAnimation(0.05);
  const { ref: industriesRef, isVisible: industriesVisible } = useScrollAnimation(0.05);

  return (
    <Layout>
      <JsonLd data={getOrganizationSchema()} />
      <JsonLd data={getWebPageSchema('About Infinite Consultant & Services | Our Story & Team', 'Learn about Infinite Consultant & Services — Bangalore based property solutions company with 12+ years of experience in landscaping interiors construction and maintenance.', 'https://infiniteconsultant.com/about')} />
      <JsonLd data={getBreadcrumbSchema([{ name: 'Home', url: 'https://infiniteconsultant.com/' }, { name: 'About Us', url: 'https://infiniteconsultant.com/about' }])} />
      {/* Hero */}
      <section className="relative h-[350px] md:h-[450px] w-full overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20Indian%20team%20of%20architects%20and%20construction%20engineers%20collaborating%20around%20a%20blueprint%20table%20in%20a%20modern%20Bangalore%20office%20overlooking%20a%20construction%20site%20natural%20light%20Indian%20corporate%20photography&width=1600&height=800&seq=about-hero&orientation=landscape&nocache=true"
          alt="Infinite Consultant & Services Team"
          className="absolute inset-0 w-full h-full object-cover object-top animate-reveal"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/60 via-foreground-950/40 to-foreground-950/70" />
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-4 sm:px-6 lg:px-10 pb-10 md:pb-14">
            <div className="max-w-6xl mx-auto">
              <nav className="flex items-center gap-2 text-white/80 text-sm mb-4" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-white transition-colors whitespace-nowrap cursor-pointer">Home</Link>
                <span className="text-white/50">/</span>
                <span className="text-white whitespace-nowrap">About Us</span>
              </nav>
              <h1 className="font-heading font-bold text-2xl md:text-4xl lg:text-5xl text-white mb-3" style={{ opacity: 0, animation: 'fadeUpSoft 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.1s forwards' }}>
                About {companyInfo.name}
              </h1>
              <p className="text-sm md:text-lg text-white/80 max-w-2xl" style={{ opacity: 0, animation: 'fadeUpSoft 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.25s forwards' }}>
                Learn about our journey, our team, and what drives us to deliver excellence in every project.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section ref={storyRef} className={`py-14 md:py-20 bg-background-50 transition-all duration-700 ${storyVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-3xl">
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-5">Our Story</h2>
              <div className="space-y-4">
                {aboutData.paragraphs.map((p, i) => (
                  <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    {p}
                  </p>
                ))}
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  Founded with a vision to simplify property ownership, we have grown into one of Bangalore&apos;s most reliable property services companies. Our commitment to quality, transparency, and customer satisfaction has earned us the trust of over 500 clients across the city, from individual homeowners to large commercial establishments.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section ref={missionRef} className="py-14 md:py-20 bg-background-100">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {missionValues.map((item, i) => (
                <div
                  key={i}
                  className={`bg-white rounded-xl p-6 md:p-8 border border-background-200/70 text-center hover:shadow-sm transition-all duration-500 ${missionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                  style={{ transitionDelay: `${i * 0.1}s` }}
                >
                  <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-gradient-brand flex items-center justify-center">
                    <i className={`${item.icon} text-white text-2xl`} />
                  </div>
                  <h3 className="font-heading font-semibold text-base md:text-lg text-foreground-950 mb-3">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section ref={statsRef} className="py-14 md:py-20 bg-primary-500">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              <StatCounter value={50} label="Expert Team Members" isVisible={statsVisible} />
              <StatCounter value={12} label="Years of Experience" isVisible={statsVisible} />
              <StatCounter value={500} label="Projects Completed" isVisible={statsVisible} />
              <StatCounter value={100} label="Client Satisfaction" isVisible={statsVisible} />
            </div>
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section ref={apartRef} className="py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-2 text-center">What Sets Us Apart</h2>
            <p className="text-sm text-foreground-500 mb-10 md:mb-12 text-center max-w-xl mx-auto">The qualities that make us the preferred choice for property solutions in Bangalore.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {whatSetsUsApart.map((item, i) => (
                <div
                  key={i}
                  className={`bg-white rounded-xl p-5 md:p-6 border border-background-200/70 hover:shadow-sm transition-all duration-500 ${apartVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                  style={{ transitionDelay: `${i * 0.08}s` }}
                >
                  <div className="w-10 h-10 rounded-lg bg-accent-50 flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-accent-600 text-lg`} />
                  </div>
                  <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section ref={industriesRef} className="py-14 md:py-20 bg-background-100">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-2 text-center">Industries We Serve</h2>
            <p className="text-sm text-foreground-500 mb-10 md:mb-12 text-center max-w-xl mx-auto">From residential to commercial, we deliver excellence across diverse property types.</p>
            <div className="flex flex-wrap justify-center gap-3">
              {industries.map((item, i) => (
                <span
                  key={i}
                  className={`px-4 py-2.5 rounded-full bg-white border border-background-200/70 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-500 transition-all duration-500 cursor-default ${industriesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                  style={{ transitionDelay: `${i * 0.04}s` }}
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20 bg-gradient-brand">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-white mb-4">
              Let&apos;s Build Something Great Together
            </h2>
            <p className="text-sm md:text-base text-white/80 max-w-xl mx-auto mb-8">
              Whether it&apos;s your dream home, a commercial property, or an industrial project — we are ready to help.
            </p>
            <Link
              to="/contact"
              className="btn-ripple inline-flex items-center gap-2 px-8 py-3.5 rounded-lg bg-white text-primary-600 font-semibold text-sm hover:bg-background-50 transition-all duration-300 hover:scale-[1.03] whitespace-nowrap cursor-pointer"
            >
              Get in Touch
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </div>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}